/**
 * iter303 — Safe response parser.
 *
 * Reads the body EXACTLY ONCE and tolerates:
 *   • Valid JSON (application/json)
 *   • Empty 204 / 205 responses
 *   • Non-JSON gateway / CDN errors (HTML, plain text)
 *   • Aborted / opaque responses
 *
 * Always returns `{ ok, status, data }`. Never throws.
 *
 * Why a single helper:
 *   The Wassla codebase has multiple places that called
 *   `await response.json()` immediately followed by reading the body a
 *   second time (or a third-party wrapper reading it underneath us).
 *   On any code path that reaches the second read the browser throws
 *   "Failed to execute 'json' on 'Response': body stream already read".
 *   Using this helper guarantees the body is consumed once via `.text()`
 *   and then parsed in memory.
 */

export async function safeReadResponse(response) {
  let raw = "";
  try {
    raw = await response.text();
  } catch (_e) {
    // Body was already consumed elsewhere (or network closed). Return what we have.
    return { ok: response.ok, status: response.status, data: null, raw: "" };
  }
  let data = null;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      // Non-JSON body (HTML 5xx page, plain text error, etc.).
      data = { raw };
    }
  }
  return { ok: response.ok, status: response.status, data, raw };
}

/**
 * Friendly user-facing error message for a `safeReadResponse` result.
 * Keeps the backend message when present, otherwise falls back to a
 * localized neutral message — never exposes raw browser errors.
 */
export function pickErrorMessage(parsed, fallback) {
  if (!parsed) return fallback;
  const d = parsed.data || {};
  const detail = d.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
  }
  if (typeof d.message === "string" && d.message) return d.message;
  if (typeof d.error === "string" && d.error) return d.error;
  return fallback;
}
